import { useState } from "react";
import { apiRequest } from "@/lib/queryClient";
import { getVisitorId, getSessionId } from "@/hooks/use-page-tracking";
import { markSubscribed } from "@/lib/signup-state";

type Props = {
  source?: string;
  className?: string;
  compact?: boolean;
  onSuccess?: () => void;
};

export default function EmailSignupForm({ className = "", compact = false, onSuccess }: Props) {
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (status === "loading") return;
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setStatus("error");
      setMessage("Please enter a valid email address.");
      return;
    }
    const trimmedPhone = phone.trim();
    if (!trimmedPhone || (trimmedPhone.match(/\d/g)?.length ?? 0) < 7) {
      setStatus("error");
      setMessage("Please enter a valid phone number.");
      return;
    }
    setStatus("loading");
    setMessage("");
    try {
      await apiRequest("POST", "/api/email-signup", {
        email: trimmedEmail,
        phone: trimmedPhone,
        visitorId: getVisitorId(),
        sessionId: getSessionId(),
      });
      setStatus("success");
      setMessage("Almost there! Check your inbox to confirm your subscription.");
      setEmail("");
      setPhone("");
      // Mark the shared subscribed state so every email-capture surface
      // (savings popup, "Before you go" modal) suppresses itself from now on.
      markSubscribed();
      onSuccess?.();
    } catch (err: any) {
      setStatus("error");
      setMessage(err?.message?.replace(/^\d+:\s*/, "") || "Something went wrong. Please try again.");
    }
  }

  if (status === "success") {
    return (
      <p
        className={`text-sm leading-relaxed text-emerald-700 dark:text-emerald-400 ${className}`}
        data-testid="email-signup-success"
        role="status"
      >
        {message}
      </p>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={className} noValidate data-testid="form-email-signup">
      <div className={compact ? "flex flex-col gap-2" : "flex flex-col sm:flex-row gap-2"}>
        <input
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="Email address *"
          value={email}
          onChange={(e) => { setEmail(e.target.value); if (status === "error") setStatus("idle"); }}
          className="flex-1 border border-input rounded-lg px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring"
          aria-label="Email address"
          data-testid="input-email-signup"
        />
        <input
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          placeholder="Phone number *"
          value={phone}
          onChange={(e) => { setPhone(e.target.value); if (status === "error") setStatus("idle"); }}
          className="flex-1 border border-input rounded-lg px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring"
          aria-label="Phone number"
          data-testid="input-email-signup-phone"
        />
        <button
          type="submit"
          disabled={status === "loading"}
          className="shrink-0 inline-flex items-center justify-center rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-60"
          data-testid="button-email-signup"
        >
          {status === "loading" ? "Sending…" : "Subscribe"}
        </button>
      </div>
      {status === "error" && (
        <p className="mt-2 text-xs text-red-600 dark:text-red-400" role="alert" data-testid="email-signup-error">{message}</p>
      )}
      <p className="mt-2 text-[11px] leading-snug text-muted-foreground">
        Join our list for lake-life tips, seasonal ideas &amp; special offers. Booking direct avoids platform service fees; a 3% card processing fee applies. We'll email you to confirm. Unsubscribe anytime. By providing your phone number you consent to receive AI-generated follow-up calls or texts from Legendary Log Cabin. Reply STOP to opt out.
      </p>
    </form>
  );
}
